import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, FlatList, Keyboard, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native'
import type { LoungeMessage } from '@ppwr/convex/lounges/types'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { translate } from '../../../i18n'
import { buildChatItems, isEmojiOnly, type ChatListItem } from './loungeChatUtils'

export type { LoungeMessage }

type LoungeChatTabProps = {
  messages: LoungeMessage[] | undefined
  userId: string
  loungeId: Id<'lounges'>
  userName: string
  chatEnabled: boolean
  sendMessage: (args: {
    loungeId: Id<'lounges'>
    userId: string
    userName: string
    message: string
  }) => Promise<null>
  onJoinGame: (gameId: string) => void
  onMessageSent: (length: number) => void
}

export function LoungeChatTab({
  messages,
  userId,
  loungeId,
  userName,
  chatEnabled,
  sendMessage,
  onJoinGame,
  onMessageSent
}: LoungeChatTabProps) {
  const [messageText, setMessageText] = useState('')
  const [chatError, setChatError] = useState<string | null>(null)
  const [androidKeyboardHeight, setAndroidKeyboardHeight] = useState(0)
  const chatListRef = useRef<FlatList>(null)

  const chatItems = useMemo(() => (messages ? buildChatItems(messages) : undefined), [messages])

  // Auto-scroll on new messages
  const prevMessageCount = useRef(0)
  useEffect(() => {
    if (messages && messages.length > prevMessageCount.current) {
      prevMessageCount.current = messages.length
      setTimeout(() => chatListRef.current?.scrollToEnd({ animated: true }), 100)
    }
  }, [messages?.length])

  useEffect(() => {
    const sub = Keyboard.addListener('keyboardDidShow', () => {
      setTimeout(() => chatListRef.current?.scrollToEnd({ animated: true }), 100)
    })
    return () => sub.remove()
  }, [])

  useEffect(() => {
    if (Platform.OS !== 'android') return
    const showSub = Keyboard.addListener('keyboardDidShow', (e) => {
      setAndroidKeyboardHeight(e.endCoordinates.height)
    })
    const hideSub = Keyboard.addListener('keyboardDidHide', () => {
      setAndroidKeyboardHeight(0)
    })
    return () => {
      showSub.remove()
      hideSub.remove()
    }
  }, [])

  const handleSendMessage = useCallback(async () => {
    const trimmed = messageText.trim()
    if (!trimmed || !loungeId || !userId) return
    setChatError(null)
    setMessageText('')
    try {
      await sendMessage({ loungeId, userId, userName, message: trimmed })
      onMessageSent(trimmed.length)
    } catch (err: any) {
      const msg: string = err.message ?? ''
      if (msg.includes('Chat is disabled')) return
      setChatError(msg || 'Failed to send message')
    }
  }, [messageText, loungeId, userId, userName, sendMessage, onMessageSent])

  const renderItem = useCallback(
    ({ item }: { item: ChatListItem }) => {
      if (item.kind === 'header') {
        return (
          <View style={styles.timestampHeader}>
            <DS.Text variant="Mini/Regular" style={styles.timestampText}>
              {item.label}
            </DS.Text>
          </View>
        )
      }

      const { data: msg, isFirstInGroup, isLastInGroup } = item
      const isMe = msg.userId === userId
      const gameId = msg.gameId

      if (gameId) {
        const isOver = msg.gameState === 'finished' || msg.gameState === 'deleted'
        return (
          <Pressable
            style={[styles.gameAnnounceBubble, isOver && styles.gameAnnounceBubbleOver]}
            onPress={isOver ? undefined : () => onJoinGame(gameId)}
          >
            <DS.Icon name={isOver ? 'Close' : 'Arrow---right'} size={16} color="Global/White" />
            <DS.Text variant="Body Large/Medium" style={{ color: '#fff', flex: 1 }}>
              {isOver ? `${msg.userName}'s game has ended` : msg.message}
            </DS.Text>
          </Pressable>
        )
      }

      const emojiOnly = isEmojiOnly(msg.message)

      return (
        <View
          style={[
            styles.messageBubbleWrapper,
            isMe ? { alignSelf: 'flex-end' } : { alignSelf: 'flex-start' },
            isLastInGroup && { marginBottom: 12 }
          ]}
        >
          {isFirstInGroup && !isMe && !emojiOnly && (
            <DS.Text variant="Mini/Regular" style={styles.senderName}>
              {msg.userName}
            </DS.Text>
          )}
          <View
            style={[
              styles.messageBubble,
              isMe && !emojiOnly && styles.messageBubbleMe,
              emojiOnly && styles.messageBubbleEmoji
            ]}
          >
            <DS.Text
              variant="Body Large/Regular"
              style={emojiOnly ? styles.emojiText : isMe ? { color: DS.palette['Global/White'] } : undefined}
            >
              {msg.message}
            </DS.Text>
          </View>
          {isLastInGroup && !emojiOnly && <View style={[styles.tail, isMe ? styles.tailRight : styles.tailLeft]} />}
        </View>
      )
    },
    [userId, onJoinGame]
  )

  if (chatEnabled === false) {
    return (
      <View style={styles.disabledContainer}>
        <DS.Text variant="Body Large/Regular" style={styles.disabledText}>
          {translate('loungeChatTab.chatDisabled')}
        </DS.Text>
      </View>
    )
  }

  return (
    <View style={styles.chatContainer}>
      {chatItems === undefined ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={DS.palette['Brand/Maroon']} />
        </View>
      ) : (
        <FlatList
          ref={chatListRef}
          data={chatItems}
          renderItem={renderItem}
          keyExtractor={(item) => (item.kind === 'header' ? item.key : item.data._id)}
          style={styles.chatList}
          contentContainerStyle={styles.chatListContent}
          onContentSizeChange={() => chatListRef.current?.scrollToEnd({ animated: false })}
          keyboardDismissMode="interactive"
          keyboardShouldPersistTaps="handled"
        />
      )}
      {chatError && (
        <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Brown'], paddingHorizontal: 16 }}>
          {chatError}
        </DS.Text>
      )}
      <View style={[styles.chatInputRow, Platform.OS === 'android' && { marginBottom: 8 + androidKeyboardHeight }]}>
        <TextInput
          style={styles.chatInput}
          placeholder="Send message"
          placeholderTextColor="#B2AFA9"
          value={messageText}
          onChangeText={setMessageText}
          onSubmitEditing={handleSendMessage}
          returnKeyType="send"
          submitBehavior="submit"
        />
        <Pressable
          onPress={handleSendMessage}
          disabled={!messageText.trim()}
          style={[styles.sendButton, !messageText.trim() && { opacity: 0.4 }]}
        >
          <View style={{ transform: [{ rotate: '-90deg' }] }}>
            <DS.Icon name="Arrow---right" size={18} color="Global/White" />
          </View>
        </Pressable>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  chatContainer: {
    flex: 1
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },
  chatList: {
    flex: 1
  },
  chatListContent: {
    padding: 16
  },
  timestampHeader: {
    alignItems: 'center',
    paddingVertical: 8
  },
  timestampText: {
    color: '#8C8984'
  },
  chatInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 12,
    paddingHorizontal: 8,
    paddingVertical: 8,
    gap: 8,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 28,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 4
  },
  chatInput: {
    flex: 1,
    height: 40,
    paddingHorizontal: 12,
    backgroundColor: 'transparent',
    fontSize: 14
  },
  sendButton: {
    backgroundColor: DS.palette['Global/Black'],
    borderRadius: 18,
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center'
  },
  messageBubbleWrapper: {
    maxWidth: '80%',
    marginBottom: 2
  },
  senderName: {
    color: '#8C8984',
    marginBottom: 2,
    marginLeft: 4
  },
  messageBubble: {
    backgroundColor: '#E8E5E0',
    borderRadius: 18,
    padding: 8,
    paddingHorizontal: 12
  },
  messageBubbleMe: {
    backgroundColor: DS.palette['Brand/Maroon']
  },
  messageBubbleEmoji: {
    backgroundColor: 'transparent',
    padding: 4,
    paddingHorizontal: 4
  },
  emojiText: {
    fontSize: 32
  },
  tail: {
    position: 'absolute',
    bottom: -7,
    width: 0,
    height: 0
  },
  tailLeft: {
    left: 16,
    borderTopWidth: 8,
    borderTopColor: '#E8E5E0',
    borderRightWidth: 8,
    borderRightColor: 'transparent'
  },
  tailRight: {
    right: 16,
    borderTopWidth: 8,
    borderTopColor: DS.palette['Brand/Maroon'],
    borderLeftWidth: 8,
    borderLeftColor: 'transparent'
  },
  gameAnnounceBubble: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: DS.palette['Brand/Maroon'],
    borderRadius: 12,
    padding: 12,
    paddingHorizontal: 16,
    alignSelf: 'stretch',
    marginBottom: 12
  },
  gameAnnounceBubbleOver: {
    backgroundColor: DS.palette['Brand/Dark Grey'],
    opacity: 0.6
  },
  disabledContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },
  disabledText: {
    color: '#8C8984'
  }
})
